import { useState, useEffect, useCallback } from 'react';
import { BREAKPOINTS, type Breakpoint } from '@/lib/constants';

/**
 * Returns true when the media query string matches.
 * Syncs with resize events via matchMedia listener.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = useCallback(
    () => window.matchMedia(query).matches,
    [query],
  );

  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return getMatch();
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * Returns true when viewport width >= the given Tailwind breakpoint.
 */
export function useBreakpoint(breakpoint: Breakpoint): boolean {
  const px = BREAKPOINTS[breakpoint];
  return useMediaQuery(`(min-width: ${px}px)`);
}

/** Convenience hooks */
export const useIsMobile = () => !useBreakpoint('md');
export const useIsTablet = () => useBreakpoint('md') && !useBreakpoint('lg');
export const useIsDesktop = () => useBreakpoint('lg');
export const useIsWidescreen = () => useBreakpoint('xl');
